import {
  RcFile,
  UploadChangeParam,
  UploadFile,
} from '@ciscodesignsystems/cds-react-upload/src/lib';
import { TabsList } from 'src/app/enums/TabsList';
import { IToastData } from '../../contexts/ToasterContext';
import { EditUserNotificationType } from '../../enums/EditUser';
import { GroupType } from '../../enums/Groups';
import { RolesPermission } from '../../enums/RolesPermissionList';
import { UserRoleType } from '../../enums/UserRoles';
// eslint-disable-next-line import/no-cycle
import { IConfig, IModifiedUser } from '../../interfaces/interface';
// eslint-disable-next-line import/no-cycle
import { IRoleToGroupRelationResp } from '../../interfaces/IUserRoles';
import { IUseFormField } from '../groups/interfaces';

export interface IUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

export interface IEditUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

export interface IUserFormFieldError {
  errorStatus: boolean;
  errorMessage: string;
}

export interface IUserFormField {
  value: string;
  errorStatus: boolean;
  errorMessage: string;
}

export type IUserFormRow = Record<string, IUserFormField>;

export interface IAccumulatorCorrectErrorRows {
  correctRows: IUserFormRow[];
  errorRows: IUserFormRow[];
}

export interface IUploadFile {
  fileName: string;
  progressBar: number;
}

export interface ICreatedUsers {
  invitedUsersCount: number;
  errors: string[] | null;
  errorReason?: string | null;
}

export interface ICreateAllUsers {
  firstName: string;
  lastName: string;
  email: string;
  login: string;
}

export interface IOption {
  label: string;
  value: string;
  selected?: boolean;
  type?: string;
}

export interface ISecurityCloudControlOption {
  label: RolesPermission;
  value: RolesPermission;
  selected: boolean;
}

export type IDependentOption = Record<string, IOption[]> | any;

export interface IRolesRows {
  selectedProduct: string | null;
  productsOptions: IOption[];
  rolesOptions: IDependentOption;
}

export interface IRolesWithInstancesRows {
  selectedRole: string | null;
  rolesOptions: IOption[];
  instancesOptions: IDependentOption;
}

export interface IAction {
  type: string;
}

export interface IActionWithPayload<T = object> extends IAction {
  payload: T;
}

export interface IRbacRole {
  id: string;
  name: string;
  product: string;
  roleDisplayName: string;
  roleDescription: string;
  productKey?: string;
  isProductProvisioned: boolean;
  delegatable: boolean;
  featureFlags: string[] | null;
  type: UserRoleType;
}

export interface IRbacAssignedRole {
  id: string;
  roleId: string;
  enterpriseId: string;
  userId: string;
  tenantId: string;
  instanceName?: string;
}

export interface IRbacUserGroupAssignments {
  id: string;
  enterpriseId: string;
  groupId: string;
  userId: string;
}

export interface IAssigneRoleRow {
  id: string;
  role: string;
  product: string;
  productType: string;
  productAndRole: string;
  instanceName: string;
  region: string;
  roleType: string;
}
export interface IRbacRoleAssignments {
  enterpriseId: string;
  id: string;
  roleId: string;
  userId: string;
  tenantId: string;
}

export interface IGroupToRoleRelation {
  id: string;
  groupId: string;
  roleId: string;
  tenantId: string;
}

export interface IAssignedRole {
  roleDisplayName: string;
  roleId: string;
  tenantId: string;
  productName: string;
  regionName: string | undefined;
  delegatable: boolean;
}

export interface IGroupRolesDetailsTableData {
  assignedRole: string;
  assignedWithin: string;
  productName: string;
  delegatable: boolean;
}

export interface IRbacTableDataRole {
  id: string;
  role: string;
  product: string;
  productType: string;
  roleType: string;
}

export interface IRbacTableDataGroup {
  id: string;
  name: string;
  description: string;
  type: string;
  groupId?: string;
  assignedRoles: IAssignedRole[];
  isSharedGroup?: boolean;
}

export interface IRemoveRowAction {
  role?: IRbacRoleAssignments; // TODO do we need this type: This will be rework in https://cisco-sbg.atlassian.net/browse/CSESC-6687
  group?: IRbacUserGroupAssignments; // TODO do we need this type: This will be rework in https://cisco-sbg.atlassian.net/browse/CSESC-6687
}

export interface IRemoveButtonProps {
  roleId?: string;
  groupId?: string;
  title: string;
}

export interface IEditUsers {
  userDetails: IEditUser;
  groupsList: IRbacGroup[];
  assignedGroupsList: IRbacUserGroupAssignments[];
  rolesList: IRbacRole[];
  assignedRolesList: IRbacRoleAssignments[];
  deleteRoleId: { id: string }; // TODO what type is deleteRoleId { id: string } or string: This will be rework in https://cisco-sbg.atlassian.net/browse/CSESC-6687
  deleteGroupId: { id: string }; // TODO what type is deleteGroupId { id: string } or string: This will be rework in https://cisco-sbg.atlassian.net/browse/CSESC-6687
  activeEditUserId: string | null | undefined;
  removeModalFlag: boolean;
  isUserDetailsDrawerVisible: boolean;
  isRemoveModalVisible: boolean;
}

export interface IStepperStep {
  nextClicked: boolean;
  backClicked: boolean;
  setNextClicked: React.Dispatch<React.SetStateAction<boolean>>;
  setCancelClicked: React.Dispatch<React.SetStateAction<boolean>>;
  cancelClicked: boolean;
  updateStepState: (value: boolean) => void;
  disablePrimaryAction: (value: boolean) => void;
  setShowStepper: Function;
}

export interface IStep {
  nextClicked: boolean;
  setNextClicked: React.Dispatch<React.SetStateAction<boolean>>;
  updateStepState: (value: boolean) => void;
  setShowStepper: Function;
  disablePrimaryAction: (value: boolean) => void;
}

interface IFormField {
  value: string;
  error: string | null;
}

export interface IGroupFormState {
  name: IFormField;
  description: IFormField;
}

export interface ICreateNewGroupForm {
  nameField: IUseFormField;
  descriptionField: IUseFormField;
}

export enum IdpUserType {
  Okta = 'OKTA',
  Federated = 'FEDERATION',
  Social = 'SOCIAL',
}

export enum IdpUserTypeNames {
  OKTA = 'Local',
  FEDERATION = 'Federated',
  SHARED = 'Shared',
}

export interface IRbacGroup {
  id: string;
  enterpriseId: string;
  name: string;
  description: string;
  groupId?: string;
  type: GroupType;
  isSharedGroup?: boolean;
}

export interface IRbacGroupWithoutInstances {
  id: string;
  enterpriseId: string;
  name: string;
  description: string;
  groupId?: string;
  type: GroupType;
  isSharedGroup?: boolean;
  appliesTo?: string | undefined;
}

export interface IIndicatorPanelConfig {
  completed: boolean;
  active: boolean;
  disabled: boolean;
  stepNumber: number;
  title: string;
  key: string;
}

export interface IContentPanelConfig {
  componentName: React.FC<IStepperStep & IConfig> | any;
  primaryCTA: string;
  primaryActionDisabled: boolean;
  territaryActionAvailable: boolean;
}

export type ImportUserFile = RcFile | undefined;

export type NotificationData = {
  [key in EditUserNotificationType]: IToastData;
};

export interface IFileUpload {
  info: UploadChangeParam<UploadFile>;
  setFileError: (message: string) => void;
}

export interface IFileUploadAction {
  type: string;
  payload: IFileUpload;
}

export type RbacGroupsResponseType =
  | {
      data: {
        rbacGroups: IRbacGroup[];
        rbacUserGroups: IRbacGroup[];
      };
    }
  | any;

export type RbacAssignedGroupsResponseType =
  | {
      data: {
        rbacUserGroupsAssignments: IRbacUserGroupAssignments[];
      };
    }
  | any;

export type RbacRolesResponseType =
  | {
      data: {
        rbacRoles: IRbacRole[];
      };
    }
  | any;

export type UserInvitationsResponseType =
  | {
      data: ICreatedUsers;
    }
  | any;

export type RbacAssignedRolesResponseType =
  | {
      data: {
        rbacUserRolesAssignments: IRbacAssignedRole[];
      };
    }
  | any;

export type EditedUserResponseType =
  | {
      data: IUser;
    }
  | any;

export type OptionsType = IOption[] | any;

export interface IActionTableProps {
  data: IModifiedUser[];
  loading: boolean;
  openInviteDrawer: () => void;
  usersTableActiveTab: TabsList;
  setUsersTableActiveTab: (tab: TabsList) => void;
  tabOptions: Array<{ label: string; value: TabsList }>;
}
export interface IActionTableRef {
  handleClearSearch: () => void;
}

export interface IGroupWithRoleRelations {
  rbacGroup: IRbacGroup;
  mappedRoles: IRoleToGroupRelationResp[];
  inheritedRoles: string[];
}

export interface IGroupWithRoleRelationsResponse {
  rbacGroups: IGroupWithRoleRelations[];
}

export interface IManagedRbacGroups {
  fullyManagedGroups: IRbacGroup[];
  partiallyManagedGroups: IRbacGroup[];
  noRolesGroups: IRbacGroup[];
}
